import {Platform} from 'react-native';
import {destroy} from 'redux-form';
import {AppActions, AppThunk} from '..';
import {RegisterClientCabinetResponsePayload} from '../../../shared/models/api/registerClientCabinetResponsePayload';
import {Forms} from '../../../shared/models/forms';
import {storage, StorageKeys} from '../../../shared/storage';
import {CardApplicationStatus} from '../../../types/enums/cardApplicationStatus';
import {profileSlice} from '../../reducers/profile';
import {httpService, HttpWrapperOptions} from '../../../shared/httpWrapper';
import {apiUrls} from '../../../shared/constants/urls';
import {getApiRoute, hashPassword, onlyDigitsPipe} from '../../../shared/pipes';
import {loginSlice} from '../../reducers/login';
import {setRuntimeLoginStepState} from '../runtime/loginStep';
import {LoginState} from '../../../shared/models/login/loginStep';
import {
  AuthorizeCabinetRequestPayload,
  AuthorizeResponsePayload,
  CheckCodeRequestPayload,
  LoginResponsePayload,
  RequestedOperationType,
  SendCodeChannel,
  SendCodeChannelType,
  SendCodeRequestPayload,
  SendCodeResponsePayload,
  SendCodeWithCheckPhoneResponsePayload,
  SetPasswordRequestPayload,
  SignOutRequestPayload,
} from '../../../shared/models/api';
import {fcmService} from '../../../shared/fcmWrapper';
import {getTokenResponse} from '../../../shared/fcmWrapper/model';
import {contentSlice} from '../../reducers/resources/content';
import {EsiaResponseType} from '../../../shared/models/login';
import {loginStepSlice} from '../../reducers/runtime/loginStep';
import {AuthorizeEsiaResponsePayload} from '../../../shared/models/api/authorizeEsiaResponsePayload';
import {AuthorizeEsiaRequestPayload} from '../../../shared/models/api/authorizeEsiaRequestPayload';
import {RegisterClientCabinetRequestPayload} from '../../../shared/models/api/registerClientCabinetRequestPayload';
import {addNotification} from '../snackbar';
import {SnackbarNotificationType} from '../../../shared/components/snackbar/model';
import {setBadgeCount} from '../../../shared/fcmWrapper/pushNotification';
import {RegisterCardApplicationRequestPayload} from '../../../types/registerCardApplicationRequestPayload';
import {ActivateCardRequestPayload} from '../../../types/activateCardRequestPayload';
import {assertSamePerson, prepareCardReissueAfterEsia, showEsiaMismatchAlert} from '../cardReissue';
import {enterCardActivationSession, loadCardActivationSnapshot, restorePendingCardActivation} from '../cardActivation';
import {mergeEsiaWithServerRegInfo} from '../../../screens/login/cardActivation';
import {
  bindCardReissueEsiaOAuthState,
  captureCardReissueEsiaAttempt,
  clearCardReissueEsiaPending,
  isCardReissueEsiaAttemptCurrent,
} from '../../../shared/storage/cardReissueEsia';
import {navigateToCardReissue} from '../../../shared/routing/cardReissueEsia';
import {hideGlobalLoader} from '../../../shared/utils/globalLoader';
import {queueEsiaLogoutForCardReissue} from '../../../shared/components/esiaLogoutWebView';
import {UpdateCardApplicationRequestPayload} from '../../../types/updateCardApplicationRequestPayload';
import {DeliveryType} from '../../../types/registerCardApplicationRequestPayload';
import {getCabinetAuthHeaders} from '../../../shared/utils/cabinetAuthToken';

export const setLoginResponse = (response: LoginResponsePayload): AppThunk => {
  return (dispatch) => {
    dispatch(loginSlice.actions.setLoginResponse(response));
  };
};

export const setLoginValue = (login: string): AppThunk => {
  return (dispatch) => {
    dispatch(loginSlice.actions.setLogin(login));
  };
};

export const checkLogin = (login: string): AppThunk => {
  return async (dispatch) => {
    let response: LoginResponsePayload | null = null;
    response = await httpService.get<LoginResponsePayload>(
      getApiRoute(apiUrls.login, {login: login || ''}),
      new HttpWrapperOptions().Errors(false).ThrowException(true).Wait(true),
    );
    dispatch(loginSlice.actions.setLogin(login));
    dispatch(setLoginResponse(response));
    dispatch(loginSlice.actions.setLoginResponse(response));
    dispatch(loginSlice.actions.setSendCodeResponse(null));
  };
};

export const login = (password: string): AppThunk => {
  return async (dispatch, getState) => {
    const state = getState();
    const passHash = hashPassword(password);
    let fcmToken: getTokenResponse | null = null;

    try {
      fcmToken = await fcmService.getToken();
    } catch (error) {
      console.error(error);
    }

    const authResponse = await httpService.post<AuthorizeResponsePayload>(
      getApiRoute(apiUrls.auth),
      {
        login: onlyDigitsPipe(state.login.login ?? ''),
        loginType: state.login.loginResponse?.loginType,
        passHash,
        firebaseId: fcmToken?.token || null,
        previousFirebaseId: fcmToken?.previousToken || null,
      } as AuthorizeCabinetRequestPayload,
      new HttpWrapperOptions().Wait(true),
    );
    if (authResponse) {
      await storage.setItem(StorageKeys.token, authResponse.token);
      await storage.removeItem(StorageKeys.isESIA);

      const isPendingActivation = await dispatch(restorePendingCardActivation());
      if (isPendingActivation) {
        return;
      }

      if (Platform.OS === 'web') {
        dispatch(profileSlice.actions.setAuthenticationState(true));
      } else {
        dispatch(profileSlice.actions.setPinCode(null));
      }
      dispatch(profileSlice.actions.setToken(authResponse.token));
      dispatch(setRuntimeLoginStepState(LoginState.enter));
      dispatch(resetLoginState());
      dispatch(profileSlice.actions.setSessionState(false));
    }
  };
};

export const registration = (password: string, code: string): AppThunk => {
  return async (dispatch, getState) => {
    const state = getState();
    const passwordHash = hashPassword(password);
    let fcmToken: getTokenResponse | null = null;

    try {
      fcmToken = await fcmService.getToken();
    } catch (error) {
      console.error(error);
    }

    const newPasswordResponse = await httpService.post<AuthorizeResponsePayload>(
      getApiRoute(apiUrls.newPass),
      {
        passwordHash,
        code,
        codeId: state.login.sendCodeResponse?.codeId,
        firebaseId: fcmToken?.token || null,
        previousFirebaseId: fcmToken?.previousToken || null,
      } as SetPasswordRequestPayload,
      new HttpWrapperOptions().Wait(true),
    );
    if (newPasswordResponse) {
      await storage.setItem(StorageKeys.token, newPasswordResponse.token);
      if (Platform.OS === 'web') {
        dispatch(profileSlice.actions.setAuthenticationState(true));
      } else {
        dispatch(profileSlice.actions.setPinCode(null));
      }
      dispatch(profileSlice.actions.setToken(newPasswordResponse.token));
      dispatch(setRuntimeLoginStepState(LoginState.enter));
      dispatch(resetLoginState());
      dispatch(profileSlice.actions.setSessionState(false));
    }
  };
};

export const createNewPassword = (code: string, password: string, isNewPhoneRegistration: boolean): AppThunk => {
  return async (dispatch, getState) => {
    const state = getState();
    const passwordHash = hashPassword(password);
    let fcmToken: getTokenResponse | null = null;

    try {
      fcmToken = await fcmService.getToken();
    } catch (error) {
      console.error(error);
    }
    const codeId = await storage.getItem<number>(StorageKeys.recoveryCodeId);
    const newPasswordResponse = await httpService.post<AuthorizeResponsePayload>(
      getApiRoute(apiUrls.newPass),
      {
        passwordHash,
        code,
        codeId: state.login.sendCodeResponse?.codeId ?? codeId,
        firebaseId: fcmToken?.token || null,
        previousFirebaseId: fcmToken?.previousToken || null,
        requestedOperation: isNewPhoneRegistration
          ? RequestedOperationType.SetPhoneNumberConfirmation
          : RequestedOperationType.SetPasswordConfirmation,
      } as SetPasswordRequestPayload,
      new HttpWrapperOptions().Wait(true),
    );
    if (newPasswordResponse) {
      await storage.setItem(StorageKeys.token, newPasswordResponse.token);
      if (Platform.OS === 'web') {
        dispatch(profileSlice.actions.setAuthenticationState(true));
      } else {
        dispatch(profileSlice.actions.setPinCode(null));
      }
      dispatch(profileSlice.actions.setToken(newPasswordResponse.token));
      dispatch(setRuntimeLoginStepState(LoginState.enter));
      dispatch(resetLoginState());
      dispatch(profileSlice.actions.setSessionState(false));
      dispatch(profileSlice.actions.setHasPassword(true));
    }
  };
};

export const createNewPinCode = (pinCode: string): AppThunk => {
  return async (dispatch) => {
    await storage.setItem(StorageKeys.pinCode, pinCode);
    dispatch(profileSlice.actions.setPinCode(pinCode));
  };
};

export const logout = (): AppThunk => {
  return async (dispatch) => {
    await clearCardReissueEsiaPending();
    let fcmToken: string | null = null;
    try {
      fcmToken = (await fcmService.getToken()).token;
    } catch {
      fcmToken = await storage.getItem<string>(StorageKeys.fcmToken);
    }
    try {
      if (fcmToken) {
        await httpService.post<void>(
          getApiRoute(apiUrls.signOut),
          {
            firebaseId: fcmToken,
          } as SignOutRequestPayload,
          new HttpWrapperOptions().Errors(false).ThrowException(true).Wait(true),
        );
        await fcmService.cleanToken();
      }
    } catch (error) {
      console.error(error);
    } finally {
      dispatch({type: AppActions.Reset});
      dispatch(profileSlice.actions.reset(null));
      await storage.removeItem(StorageKeys.pinCode);
      await storage.removeItem(StorageKeys.isESIA);
      await storage.removeItem(StorageKeys.activationToken);
      await storage.removeItem(StorageKeys.esiaRegInfo);
      dispatch(profileSlice.actions.setSessionState(false));
      await storage.removeItem(StorageKeys.token);
      // await storage.removeItem(StorageKeys.api);
      await dispatch(contentSlice.actions.reset(null));
      dispatch(destroy(Forms.acceptorsFilterForm));
      setBadgeCount(0);
    }
  };
};

export const logoutEsiaSession = (): AppThunk<string | null> => {
  return async () => {
    let link: string | null = null;
    try {
      const response = await httpService.get<{link: string}>(
        getApiRoute(apiUrls.esiaLogout),
        new HttpWrapperOptions().WaitTimeout(0).Wait(false).Errors(false).ThrowException(false),
      );
      link = response?.link ?? null;
    } catch {
      link = null;
    } finally {
      await storage.removeItem(StorageKeys.isESIA);
    }
    return link;
  };
};

const abortCardReissueEsiaMismatch = (): AppThunk => {
  return async (dispatch) => {
    dispatch(showEsiaMismatchAlert());
    const clearing = clearCardReissueEsiaPending();
    const rejectedAttempt = captureCardReissueEsiaAttempt();
    await clearing;
    if (!isCardReissueEsiaAttemptCurrent(rejectedAttempt)) return;
    const logoutLink = (await dispatch(logoutEsiaSession())) as string | null;
    if (!isCardReissueEsiaAttemptCurrent(rejectedAttempt)) return;
    hideGlobalLoader({resetBlockingCalls: true});

    // Hand off logout to CardReissue so buttons stay disabled until redirect.
    if (logoutLink && Platform.OS === 'web') {
      queueEsiaLogoutForCardReissue(logoutLink);
    }

    navigateToCardReissue();
  };
};

export const enter = (token: string): AppThunk => {
  return async (dispatch) => {
    dispatch(profileSlice.actions.setToken(token));
  };
};

export const resetLoginState = (): AppThunk => {
  return async (dispatch) => {
    dispatch(loginSlice.actions.reset(null));
  };
};

export const getNewCode = (
  numCardESK: string,
  showStandardError: boolean,
  channel: SendCodeChannelType,
  operationType: 'create' | 'restore' | undefined = 'restore',
): AppThunk => {
  return async (dispatch) => {
    const response = await httpService.post<SendCodeResponsePayload>(
      getApiRoute(apiUrls.sendCode),
      {
        channel,
        requestedOperation: RequestedOperationType.SetPasswordConfirmation,
        numCardESK,
        operationType,
      } as SendCodeRequestPayload,
      new HttpWrapperOptions().Wait(true).Errors(showStandardError).ThrowException(true),
    );
    if (response) {
      dispatch(loginSlice.actions.setSendCodeResponse(response));
      if (response?.codeId && channel === SendCodeChannel.Email) {
        await storage.setItem(StorageKeys.recoveryCodeId, response?.codeId);
      }
    }
  };
};

export const checkCode = (code: string, mobilePhone: string, requestedOperation: RequestedOperationType): AppThunk => {
  return async (dispatch, getState) => {
    const state = getState();
    const codeId = await storage.getItem<number>(StorageKeys.recoveryCodeId);
    await httpService.post(
      getApiRoute(apiUrls.checkCode),
      {
        codeId: state.login.sendCodeResponse?.codeId ?? codeId,
        code,
        requestedOperation,
        mobilePhone,
      } as CheckCodeRequestPayload,
      new HttpWrapperOptions().Wait(true).Errors(false).ThrowException(true),
    );
  };
};

export const sendCodeWithCheckPhone = (
  cardNum: string,
  snils: string,
  mobilePhone: string,
  showStandardError: boolean,
): AppThunk => {
  return async (dispatch) => {
    const response = await httpService.post<SendCodeWithCheckPhoneResponsePayload>(
      getApiRoute(apiUrls.sendCodeWithCheckPhone),
      {
        cardNum,
        snils,
        mobilePhone,
      } as SendCodeWithCheckPhoneResponsePayload,
      new HttpWrapperOptions().Wait(true).Errors(showStandardError).ThrowException(true),
    );
    if (response && response.codeId) {
      dispatch(loginSlice.actions.setSendCodeResponse(response));
    }
  };
};

export const getEsiaLink = ({
  showError = true,
  loaderText = '',
  fromReissue = false,
}: {showError?: boolean; loaderText?: string; fromReissue?: boolean} = {}): AppThunk => {
  return async (dispatch) => {
    const clearing = Platform.OS === 'web' && !fromReissue ? clearCardReissueEsiaPending() : Promise.resolve();
    const attempt = captureCardReissueEsiaAttempt();
    await clearing;
    if (!isCardReissueEsiaAttemptCurrent(attempt) || (fromReissue && !attempt.fromReissue)) return;
    try {
      const response = await httpService.get<{link: string}>(
        getApiRoute(apiUrls.esiaGetLink),
        new HttpWrapperOptions()
          .Errors(showError)
          .ThrowException(showError)
          .WaitTimeout(0)
          .Wait(true)
          .AddText(loaderText),
      );
      if (!isCardReissueEsiaAttemptCurrent(attempt)) return;
      if (!response.link) throw new Error();
      if (Platform.OS === 'web' && fromReissue && !bindCardReissueEsiaOAuthState(attempt, response.link)) {
        throw new Error();
      }

      const isProduction = process.env.NODE_ENV === 'production';
      const isMobile = Platform.OS !== 'web';

      dispatch(
        loginSlice.actions.setEsiaLink(
          isProduction || isMobile
            ? response.link
            : response.link.replace(
                'redirect_uri=https%3A%2F%2Ftest-lk.eskso.ru',
                'redirect_uri=http%3A%2F%2Flocalhost:3000',
              ),
        ),
      );

      dispatch(setRuntimeLoginStepState(LoginState.esiaLogin));
    } catch (e) {
      if (!isCardReissueEsiaAttemptCurrent(attempt)) return;
      if (fromReissue) {
        await clearCardReissueEsiaPending();
      }
      dispatch(
        addNotification({
          message: 'Не удалось получить ссылку ЕСИА',
          type: SnackbarNotificationType.Error,
        }),
      );
      dispatch(loginSlice.actions.setEsiaLink(null));
      dispatch(setRuntimeLoginStepState(LoginState.enter));
    }
  };
};

export const esiaLogin = (): AppThunk => {
  return async (dispatch, getState) => {
    const attempt = captureCardReissueEsiaAttempt(getState().profile.holder?.snils);
    const {code, state} = getState().login.esiaCode;
    if (Platform.OS === 'web' && attempt.fromReissue && (!attempt.oauthState || attempt.oauthState !== state)) return;
    let fcmToken: getTokenResponse | null = null;
    try {
      fcmToken = await fcmService.getToken();
    } catch (error) {
      console.log(error);
    }
    if (!isCardReissueEsiaAttemptCurrent(attempt)) return;
    const data: AuthorizeEsiaRequestPayload = {
      code: code!,
      state: state!,
      firebaseId: fcmToken?.token ?? null,
      previousFirebaseId: fcmToken?.previousToken ?? null,
    };

    try {
      const res = await httpService.post<AuthorizeEsiaResponsePayload>(
        getApiRoute(apiUrls.esiaLogin),
        data,
        new HttpWrapperOptions().WaitTimeout(0).Wait(true).ThrowException(true).Errors(false),
      );

      if (!isCardReissueEsiaAttemptCurrent(attempt)) return;
      if (attempt.fromReissue) {
        const isSamePerson = await dispatch(assertSamePerson(res, attempt.expectedSnils));
        if (!isCardReissueEsiaAttemptCurrent(attempt)) return;
        if (!isSamePerson) {
          await dispatch(abortCardReissueEsiaMismatch());
          return;
        }
      }

      switch (res?.type) {
        case EsiaResponseType.Authorization: {
          const serverRegInfo = await loadCardActivationSnapshot(res.token);
          if (!isCardReissueEsiaAttemptCurrent(attempt)) return;
          if (attempt.fromReissue) {
            await dispatch(prepareCardReissueAfterEsia(true, res, attempt));
            if (!isCardReissueEsiaAttemptCurrent(attempt)) return;
          }
          if (serverRegInfo) {
            const activationRegInfo = mergeEsiaWithServerRegInfo(res, serverRegInfo);
            await storage.setItem(StorageKeys.activationToken, res.token);
            if (!isCardReissueEsiaAttemptCurrent(attempt)) return;
            await storage.setItem(StorageKeys.isESIA, true);
            if (!isCardReissueEsiaAttemptCurrent(attempt)) return;
            await storage.setItem(StorageKeys.esiaRegInfo, activationRegInfo);
            if (!isCardReissueEsiaAttemptCurrent(attempt)) return;
            dispatch(loginSlice.actions.setEsiaRegistrationInfo(activationRegInfo));
            dispatch(loginSlice.actions.setEsiaUserHasNoCards(true));
            dispatch(loginStepSlice.actions.setLoginStep(LoginState.esiaRegCardUnconfirmed));
          } else {
            await storage.setItem(StorageKeys.token, res.token);
            if (!isCardReissueEsiaAttemptCurrent(attempt)) return;
            await storage.setItem(StorageKeys.isESIA, true);
            if (!isCardReissueEsiaAttemptCurrent(attempt)) return;

            if (Platform.OS === 'web') {
              dispatch(profileSlice.actions.setAuthenticationState(true));
            } else {
              dispatch(profileSlice.actions.setPinCode(null));
            }

            dispatch(loginSlice.actions.setEsiaUserHasNoCards(false));
            dispatch(profileSlice.actions.setToken(res.token));

            if (res.cardApplicationStatusId === CardApplicationStatus.Declined) {
              dispatch(loginStepSlice.actions.setLoginStep(LoginState.esiaRegCardUnconfirmed));
            } else {
              dispatch(loginStepSlice.actions.setLoginStep(LoginState.enter));
            }

            dispatch(loginSlice.actions.resetEsiaFlow(null));
            dispatch(loginSlice.actions.setEsiaRegistrationInfo(res));
            dispatch(profileSlice.actions.setSessionState(false));
          }
          break;
        }
        case EsiaResponseType.Registration:
          if (attempt.fromReissue) {
            await dispatch(prepareCardReissueAfterEsia(true, res, attempt));
            if (!isCardReissueEsiaAttemptCurrent(attempt)) return;
          }
          await storage.setItem(StorageKeys.isESIA, true);
          if (!isCardReissueEsiaAttemptCurrent(attempt)) return;
          dispatch(loginSlice.actions.setEsiaRegistrationInfo(res));
          dispatch(loginStepSlice.actions.setLoginStep(LoginState.esiaRegCardUnconfirmed));
          break;
      }
    } catch (e: any) {
      if (!isCardReissueEsiaAttemptCurrent(attempt)) return;
      if (Array.isArray(e?.data) && e.data.length && e.data[0]?.code === 3) {
        dispatch(loginSlice.actions.setEsiaUserNotAccept(true));
        dispatch(loginStepSlice.actions.setLoginStep(LoginState.esiaLogin));
      }
    }
  };
};

export const esiaLoginFromActiveUser = (): AppThunk => {
  return async (dispatch, getState) => {
    const attempt = captureCardReissueEsiaAttempt(getState().profile.holder?.snils);
    const {code, state} = getState().login.esiaCode;
    if (Platform.OS === 'web' && attempt.fromReissue && (!attempt.oauthState || attempt.oauthState !== state)) return;
    let fcmToken: getTokenResponse | null = null;
    try {
      fcmToken = await fcmService.getToken();
    } catch (error) {
      console.log(error);
    }

    if (!isCardReissueEsiaAttemptCurrent(attempt)) return;
    const data: AuthorizeEsiaRequestPayload = {
      code: code!,
      state: state!,
      firebaseId: fcmToken?.token ?? null,
      previousFirebaseId: fcmToken?.previousToken ?? null,
    };

    try {
      const res = await httpService.post<AuthorizeEsiaResponsePayload>(
        getApiRoute(apiUrls.esiaLogin),
        data,
        new HttpWrapperOptions().WaitTimeout(0).Wait(true).ThrowException(false).Errors(false),
      );

      if (!isCardReissueEsiaAttemptCurrent(attempt)) return;
      if (attempt.fromReissue) {
        const isSamePerson = await dispatch(assertSamePerson(res, attempt.expectedSnils));
        if (!isCardReissueEsiaAttemptCurrent(attempt)) return;
        if (!isSamePerson) {
          await dispatch(abortCardReissueEsiaMismatch());
          return;
        }
      }

      switch (res?.type) {
        case EsiaResponseType.Authorization: {
          const serverRegInfo = await loadCardActivationSnapshot(res.token);
          if (!isCardReissueEsiaAttemptCurrent(attempt)) return;
          if (attempt.fromReissue) {
            await dispatch(prepareCardReissueAfterEsia(true, res, attempt));
            if (!isCardReissueEsiaAttemptCurrent(attempt)) return;
          }
          const activationRegInfo = serverRegInfo ? mergeEsiaWithServerRegInfo(res, serverRegInfo) : res;
          await storage.setItem(StorageKeys.activationToken, res.token);
          if (!isCardReissueEsiaAttemptCurrent(attempt)) return;
          await storage.setItem(StorageKeys.isESIA, true);
          if (!isCardReissueEsiaAttemptCurrent(attempt)) return;
          dispatch(loginSlice.actions.setEsiaRegistrationInfo(activationRegInfo));
          if (serverRegInfo) {
            await dispatch(enterCardActivationSession({serverRegInfo}));
          }
          break;
        }
        case EsiaResponseType.Registration:
          if (attempt.fromReissue) {
            await dispatch(prepareCardReissueAfterEsia(true, res, attempt));
            if (!isCardReissueEsiaAttemptCurrent(attempt)) return;
          }
          dispatch(loginSlice.actions.setEsiaRegistrationInfo(res));
          dispatch(loginStepSlice.actions.setLoginStep(LoginState.esiaRegCardUnconfirmed));
          break;
        default:
          return;
      }
    } catch (e: any) {}
  };
};

export const esiaRegistration = async (
  regData: RegisterClientCabinetRequestPayload,
): Promise<RegisterClientCabinetResponsePayload> => {
  return httpService.post<RegisterClientCabinetResponsePayload>(
    getApiRoute(apiUrls.esiaRegistration),
    regData,
    // Errors handled by map/form callers (notification + modal) — avoid raw axios text via displayError.
    new HttpWrapperOptions().Wait(true).ThrowException(true).Errors(false),
  );
};

export const esiaRegisterCardApplication = async (
  regData: RegisterCardApplicationRequestPayload,
  isShowError = false,
): Promise<void> => {
  const cabinetToken = await storage.getItem<string>(StorageKeys.token);
  const activationToken = await storage.getItem<string>(StorageKeys.activationToken);
  const token = cabinetToken || activationToken;
  const options = new HttpWrapperOptions().Errors(isShowError).Wait(true).ThrowException(true);

  if (token) {
    options.Headers({Authorization: `Bearer ${token}`});
  }

  return httpService.post(getApiRoute(apiUrls.esiaRegisterCardApplication), regData, options);
};

export const esiaSendActivateCard = (payload: ActivateCardRequestPayload): AppThunk => {
  return async () => {
    const token = await storage.getItem(StorageKeys.activationToken);
    await httpService.post(
      getApiRoute(apiUrls.esiaActivateCard),
      payload,
      new HttpWrapperOptions()
        .Headers({Authorization: `Bearer ${token}`})
        .Errors(false)
        .Wait(true)
        .ThrowException(true),
    );
  };
};

export const esiaSendActivateCardAfterLogged = (payload: ActivateCardRequestPayload): AppThunk => {
  return async () => {
    const token = (await storage.getItem(StorageKeys.token)) ?? (await storage.getItem(StorageKeys.activationToken));
    await httpService.post(
      getApiRoute(apiUrls.esiaActivateCard),
      payload,
      new HttpWrapperOptions()
        .Headers({Authorization: `Bearer ${token}`})
        .Errors(false)
        .Wait(true)
        .ThrowException(true),
    );
  };
};

export const updateCardApplication = (payload: UpdateCardApplicationRequestPayload): AppThunk => {
  return async () => {
    const headers = await getCabinetAuthHeaders();

    await httpService.put(
      getApiRoute(apiUrls.updateCardApplication),
      payload,
      new HttpWrapperOptions().Headers(headers).Wait(true).ThrowException(true),
    );
  };
};

export const changeCardApplicationDeliveryToIssuePoint = (payload: UpdateCardApplicationRequestPayload): AppThunk => {
  return async (dispatch, getState) => {
    await dispatch(updateCardApplication(payload));

    const esiaRegInfo = getState().login.esiaRegistrationInfo;
    if (esiaRegInfo?.cardDeliveryInfo) {
      dispatch(
        loginSlice.actions.setEsiaRegistrationInfo({
          ...esiaRegInfo,
          cardDeliveryInfo: {
            ...esiaRegInfo.cardDeliveryInfo,
            deliveryTypeId: DeliveryType.IssuePoint,
            counterpartyDepartmentId: payload.counterpartyDepartmentId,
            desiredAddress: payload.desiredAddress,
          },
        }),
      );
    }

    const profile = getState().profile.holder;
    if (profile) {
      dispatch(
        profileSlice.actions.setHolderProfile({
          ...profile,
          prerequestedCardApplicationDeliveryTypeId: DeliveryType.IssuePoint,
        }),
      );
    }
  };
};

export const changeCardApplicationDeliveryToRussianPost = (payload: UpdateCardApplicationRequestPayload): AppThunk => {
  return async (dispatch, getState) => {
    await dispatch(updateCardApplication(payload));

    const esiaRegInfo = getState().login.esiaRegistrationInfo;
    if (esiaRegInfo?.cardDeliveryInfo) {
      dispatch(
        loginSlice.actions.setEsiaRegistrationInfo({
          ...esiaRegInfo,
          cardDeliveryInfo: {
            ...esiaRegInfo.cardDeliveryInfo,
            deliveryTypeId: DeliveryType.RussianPost,
            desiredAddress: payload.desiredAddress,
          },
        }),
      );
    }

    const profile = getState().profile.holder;
    if (profile) {
      dispatch(
        profileSlice.actions.setHolderProfile({
          ...profile,
          prerequestedCardApplicationDeliveryTypeId: DeliveryType.RussianPost,
        }),
      );
    }
  };
};
